import {
  faParking,
  faSignOutAlt,
  faUserFriends,
  faHomeAlt,
  faCar,
  faBuilding,
  faHardDrive,
  faEnvelope,
  faTachographDigital,
  faMicrochip,
} from "@fortawesome/free-solid-svg-icons";
import { getAuth, signOut } from "firebase/auth";
import { useContext } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import NavItem from "components/NavItem";
import color from "constants/color";
import { useFirebaseContext } from "contexts/FirebaseContext";
import UserContext from "contexts/UserContext";
import { ReactComponent as AimoParkingSvg } from "images/aimoParking.svg";
import { ReactComponent as LanditSvg } from "images/landit.svg";
import { ReactComponent as PitPortSvg } from "images/pitPort.svg";
import AimoParking from "pages/AimoParking";
import Home from "pages/Home";
import Login from "pages/Login";
import PitPort from "pages/PitPort";
import FirebaseProvider from "providers/FirebaseProvider";
import PageStateProvider from "providers/PageStateProvider";
import UserProvider from "providers/UserProvider";

// パーツ定義
const Container = styled.div`
  display: flex;
  min-height: 100vh;
`;
const SideMenu = styled.header`
  display: flex;
  flex-direction: column;
  padding: 0 16px 14px 0;
  width: 240px;
  background-color: ${color.white};
  justify-content: space-between;
  flex: 0;
`;
const Setting = styled.div`
  margin-top: 40px;
`;
const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: ${color.border};
  margin-top: 20px;
  margin-bottom: 10px;
`;
const LanditLogo = styled(LanditSvg)`
  margin-left: 26px;
  width: 157px;
`;
const PitPortLogo = styled(PitPortSvg)`
  margin: 10px 0 14px 22px;
  width: 74px;
`;

const AimoParkingLogo = styled(AimoParkingSvg)`
  margin: 10px 0 14px 22px;
  width: 114px;
`;

// 本体
const Layout = ({ children }: { children: JSX.Element }) => {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const navHome = () => navigate("/");
  const navPitPortUser = () => navigate("/pit_port/user");
  const navPitPortOwnerEntity = () => navigate("/pit_port/owner-entities");
  const navPitPortCustomer = () => navigate("/pit_port/customer");
  const navPitPortSystemNotification = () => navigate("/pit_port/system_notification");
  const navPitPortProperty = () => navigate("/pit_port/property");
  const navPitPortUsage = () => navigate("/pit_port/usage");
  const navCompany = () => navigate("/pit_port/company");
  const navPitPortControlBox = () => navigate("/pit_port/control_box");
  const navAimoParkingCompany = () => navigate("/aimo_parking/company");
  const navAimoParkingUser = () => navigate("/aimo_parking/user");
  const navAimoParkingSystemNotification = () => navigate("/aimo_parking/system_notification");
  const navAimoParkingEdgeDevice = () => navigate("/aimo_parking/edge_device");
  const navAimoParkingAiSoftware = () => navigate("/aimo_parking/ai_software");

  const logout = () => {
    const auth = getAuth();
    signOut(auth);
    navigate("login");
  };

  return (
    <Container>
      <SideMenu>
        <div>
          <LanditLogo />
          <NavItem onClick={navHome} label="ホーム" icon={faHomeAlt} isCurrent={pathname === "/"} />
          <Separator />
          <PitPortLogo />
          <NavItem
            onClick={navPitPortOwnerEntity}
            label="貸主"
            icon={faUserFriends}
            isCurrent={pathname.startsWith("/pit_port/owner-entities")}
          />
          <NavItem
            onClick={navPitPortUser}
            label="法人ユーザ"
            icon={faUserFriends}
            isCurrent={pathname.startsWith("/pit_port/user")}
          />
          <NavItem
            onClick={navPitPortCustomer}
            label="一般ユーザ"
            icon={faUserFriends}
            isCurrent={pathname.startsWith("/pit_port/customer")}
          />
          <NavItem
            onClick={navPitPortSystemNotification}
            label="お知らせ"
            icon={faEnvelope}
            isCurrent={pathname.startsWith("/pit_port/system_notification")}
          />
          <NavItem
            onClick={navCompany}
            label="請求先"
            icon={faBuilding}
            isCurrent={pathname.startsWith("/pit_port/company")}
          />
          <NavItem
            onClick={navPitPortProperty}
            label="駐車場"
            icon={faParking}
            isCurrent={pathname.startsWith("/pit_port/property")}
          />
          <NavItem
            onClick={navPitPortControlBox}
            label="制御BOX"
            icon={faHardDrive}
            isCurrent={pathname.startsWith("/pit_port/control_box")}
          />
          <NavItem
            onClick={navPitPortUsage}
            label="利用状況"
            icon={faCar}
            isCurrent={pathname.startsWith("/pit_port/usage")}
          />
          <Separator />
          <AimoParkingLogo />
          <NavItem
            onClick={navAimoParkingCompany}
            label="管理会社"
            icon={faBuilding}
            isCurrent={
              pathname.startsWith("/aimo_parking/company") &&
              !pathname.startsWith("/aimo_parking/company/user") &&
              !pathname.includes("company_user")
            }
          />
          <NavItem
            onClick={navAimoParkingUser}
            label="ユーザ"
            icon={faUserFriends}
            isCurrent={pathname === "/aimo_parking/user" || pathname.includes("company_user")}
          />
          <NavItem
            onClick={navAimoParkingSystemNotification}
            label="お知らせ"
            icon={faEnvelope}
            isCurrent={pathname.startsWith("/aimo_parking/system_notification")}
          />
          <NavItem
            onClick={navAimoParkingEdgeDevice}
            label="エッジデバイス"
            icon={faTachographDigital}
            isCurrent={pathname.startsWith("/aimo_parking/edge_device")}
          />
          <NavItem
            onClick={navAimoParkingAiSoftware}
            label="AIバージョン"
            icon={faMicrochip}
            isCurrent={pathname.startsWith("/aimo_parking/ai_software")}
          />
        </div>
        <Setting>
          <NavItem onClick={logout} label="ログアウト" icon={faSignOutAlt} />
        </Setting>
      </SideMenu>
      {children}
    </Container>
  );
};

// userLoadingは読み込み時必ずtrue
// localStorageからFirebase Authenticationのログイン情報を読み込んだらfalseになる
const PrivateRoute = () => {
  const { isUserLoaded } = useContext(UserContext);
  const { auth } = useFirebaseContext();

  // ユーザ情報の読込が終わっていない
  // isUserLoadedは読み込み時必ずfalse
  // localStorageからFirebase Authenticationのログイン情報を読み込んだらtrueになる
  if (!isUserLoaded) return <></>;
  // 非ログイン状態
  if (!auth.currentUser) return <Navigate to="login" replace />;
  // ログイン状態
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

// Routeを設定することで、Switch以下で切り替えられる
const AppRouting = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="login" element={<Login />} />
        <Route path="/" element={<PrivateRoute />}>
          <Route path="" element={<Home />} />
          <Route path="pit_port">
            <Route path="owner-entities">
              <Route path="" element={<PitPort.OwnerEntity />} />
              <Route path="create" element={<PitPort.CreateOwnerEntity />} />
              <Route path=":id">
                <Route element={<PitPort.TitleTabArea />}>
                  <Route path="" element={<PitPort.OwnerEntityDetail />} />
                  <Route path="owner-users">
                    <Route path="" element={<PitPort.OwnerUsers />} />
                  </Route>
                </Route>
                <Route path="owner-users">
                  <Route path="create" element={<PitPort.CreateOwnerUser />} />
                </Route>
              </Route>
              <Route path=":id/edit">
                <Route path="" element={<PitPort.EditOwnerEntity />} />
              </Route>
            </Route>
            <Route path="user">
              <Route path="" element={<PitPort.User />} />
              <Route path="create" element={<PitPort.CreateUser />} />
            </Route>
            <Route path="user/:id">
              <Route path="" element={<PitPort.EditUser />} />
            </Route>
            <Route path="customer">
              <Route path="" element={<PitPort.Customer />} />
            </Route>
            <Route path="customer/:id">
              <Route path="" element={<PitPort.EditCustomer />} />
            </Route>
            <Route path="system_notification">
              <Route path="" element={<PitPort.SystemNotification />} />
              <Route path="create" element={<PitPort.CreateSystemNotification />} />
              <Route path=":id" element={<PitPort.SystemNotificationDetail />} />
            </Route>
            <Route path="property">
              <Route path="" element={<PitPort.Property />} />
              <Route path="create" element={<PitPort.CreateProperty />} />
            </Route>
            <Route path="property/:id">
              <Route path="" element={<PitPort.EditProperty />} />
            </Route>
            <Route path="usage" element={<PitPort.Usage />} />
            <Route path="company" element={<PitPort.Company />} />
            <Route path="company/:id">
              <Route path="" element={<PitPort.EditCompany />} />
            </Route>
            <Route path="control_box">
              <Route path="" element={<PitPort.ControlBox />} />
              <Route path="create" element={<PitPort.CreateControlBox />} />
            </Route>
            <Route path="control_box/:id">
              <Route path="" element={<PitPort.EditControlBox />} />
            </Route>
          </Route>
          <Route path="aimo_parking">
            <Route path="company">
              <Route path="" element={<AimoParking.Company />} />
              <Route path="create" element={<AimoParking.UpsertCompany />} />
              <Route path=":id">
                <Route path="" element={<Navigate to="info" replace />} />
                <Route path="edit" element={<AimoParking.UpsertCompany />} />
                <Route element={<AimoParking.TitleTabArea />}>
                  <Route path="info" element={<AimoParking.CompanyDetail />} />
                  <Route path="user" element={<AimoParking.CompanyUser />} />
                </Route>
                <Route path="company_user">
                  <Route path="create" element={<AimoParking.CreateUser />} />
                  <Route path=":userId/edit" element={<AimoParking.EditUser />} />
                </Route>
              </Route>
            </Route>
            <Route path="user" element={<AimoParking.User />} />
            <Route path="system_notification">
              <Route path="" element={<AimoParking.SystemNotification />} />
              <Route path="create" element={<AimoParking.CreateSystemNotification />} />
              <Route path=":id" element={<AimoParking.SystemNotificationDetail />} />
            </Route>
            <Route path="edge_device">
              <Route path="" element={<AimoParking.EdgeDevice />} />
              <Route path="create" element={<AimoParking.CreateEdgeDevice />} />
              <Route path=":id">
                <Route path="" element={<AimoParking.EdgeDeviceDetail />} />
                <Route path="edit" element={<AimoParking.EditEdgeDevice />} />
                <Route path="ai_version" element={<AimoParking.EdgeDeviceUpdateAiVersion />} />
                <Route path="status" element={<AimoParking.EdgeDeviceUpdateStatus />} />
              </Route>
            </Route>
            <Route path="ai_software">
              <Route path="" element={<AimoParking.AiSoftware />} />
              <Route path="update" element={<AimoParking.UpdateVersion />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

// Context.Providerで囲んで子要素がContextを使えるようにする
const App = () => {
  return (
    <FirebaseProvider>
      <UserProvider>
        <PageStateProvider>
          <AppRouting />
        </PageStateProvider>
      </UserProvider>
    </FirebaseProvider>
  );
};

export default App;
